import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Layout,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import type { PublicInvitation } from '@velora/contracts';
import { ApiError, accountsApi } from '../api';

type FormValues = { name: string; email: string; password: string };

export function AcceptInvitationPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [form] = Form.useForm<FormValues>();

  const q = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => accountsApi.getInvitation(token),
    enabled: !!token,
    retry: false,
  });

  useEffect(() => {
    if (q.data && !q.data.already_accepted && !q.data.expired) {
      form.setFieldsValue({ name: q.data.invitee_name, email: q.data.invitee_email });
    }
  }, [q.data, form]);

  const accept = useMutation({
    mutationFn: (values: FormValues) => accountsApi.acceptInvitation(token, values),
  });

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <BrandStrip />
        {q.isLoading && (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        )}
        {q.isError && <InvitationError err={q.error} />}
        {q.data && !accept.data && (
          <InvitationCard
            invitation={q.data}
            form={form}
            submitting={accept.isPending}
            error={accept.error}
            onSubmit={(v) => accept.mutate(v)}
          />
        )}
        {accept.data && (
          <AcceptedCard
            orgName={accept.data.organization.name}
            institutionName={accept.data.institution.name}
            redirectTo={accept.data.redirect_to}
          />
        )}
      </div>
    </Layout>
  );
}

function BrandStrip() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 14,
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 5,
          background: '#2f6ab0',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        F
      </div>
      <Typography.Text style={{ fontWeight: 600, color: '#14213d', fontSize: 13 }}>
        FatherSchool ·{' '}
        <span style={{ color: '#5b6478', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>
          accounts.fatherschool.com
        </span>
      </Typography.Text>
    </div>
  );
}

function InvitationError({ err }: { err: unknown }) {
  const status = err instanceof ApiError ? err.status : 0;
  const isNotFound = status === 404;
  return (
    <Card>
      <Result
        status={isNotFound ? '404' : 'error'}
        title={isNotFound ? 'This invitation link does not exist' : 'Something went wrong'}
        subTitle={
          isNotFound
            ? 'It may have been mistyped, or the invitation was cancelled. Ask whoever sent it for a fresh link.'
            : `We could not reach the server. ${err instanceof Error ? err.message : String(err)}`
        }
      />
    </Card>
  );
}

function InvitationCard({
  invitation,
  form,
  submitting,
  error,
  onSubmit,
}: {
  invitation: PublicInvitation;
  form: ReturnType<typeof Form.useForm<FormValues>>[0];
  submitting: boolean;
  error: unknown;
  onSubmit: (v: FormValues) => void;
}) {
  const [dontKnow, setDontKnow] = useState(false);

  if (invitation.expired) {
    return (
      <Card>
        <Result
          status="warning"
          title="This invitation has expired"
          subTitle={
            <>
              Invitations are valid for 14 days. Ask whoever provisioned{' '}
              <b>{invitation.organization.name}</b> to send a fresh link.
            </>
          }
        />
      </Card>
    );
  }

  if (invitation.already_accepted) {
    return (
      <Card>
        <Result
          status="info"
          title="This invitation has already been used"
          subTitle={
            <>
              If it wasn't you, ask <b>{invitation.organization.name}</b>'s implementation contact
              to look into it.
            </>
          }
        />
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11.5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
            You've been invited
          </Typography.Text>
          <Typography.Title level={4} style={{ margin: '4px 0 0' }}>
            Welcome, {invitation.invitee_name.split(' ')[0]}.
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: '6px 0 0', fontSize: 13 }}>
            <b>{invitation.organization.name}</b> is ready on FatherSchool. You'll be the{' '}
            <Tag color="processing" style={{ margin: '0 2px' }}>
              {invitation.role}
            </Tag>{' '}
            of <b>{invitation.institution.name}</b> ({invitation.institution.type}).
          </Typography.Paragraph>
        </div>

        {!dontKnow && (
          <Alert
            type="info"
            showIcon
            style={{ padding: '6px 10px' }}
            message={
              <span style={{ fontSize: 12 }}>
                Set a password below. OTP + MFA ship in a later iteration —{' '}
                <a onClick={() => setDontKnow(true)}>learn more</a>.
              </span>
            }
          />
        )}
        {dontKnow && (
          <Alert
            type="info"
            showIcon
            closable
            onClose={() => setDontKnow(false)}
            message={
              <span style={{ fontSize: 12 }}>
                Today we take a name + email + password. In production, a six-digit OTP confirms
                your mobile before enabling MFA. Your password is stored as a scrypt hash — never
                in the clear.
              </span>
            }
          />
        )}

        {error != null && (
          <Alert
            type="error"
            showIcon
            message={
              error instanceof ApiError && error.status === 410
                ? 'This invitation expired while this page was open. Ask for a fresh one.'
                : 'Could not accept the invitation'
            }
            description={error instanceof Error ? error.message : String(error)}
          />
        )}

        <Form<FormValues>
          form={form}
          layout="vertical"
          disabled={submitting}
          onFinish={onSubmit}
          initialValues={{ name: invitation.invitee_name, email: invitation.invitee_email }}
        >
          <Form.Item name="name" label="Your name" rules={[{ required: true }]}>
            <Input placeholder="Ms Sharma" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="principal@apex.edu.in" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Choose a password"
            rules={[{ required: true, min: 8, message: 'at least 8 characters' }]}
          >
            <Input.Password placeholder="At least 8 characters" autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            Accept and continue
          </Button>
        </Form>

        <Typography.Text type="secondary" style={{ fontSize: 11.5, textAlign: 'center', display: 'block' }}>
          Expires{' '}
          {new Date(invitation.expires_at).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </Typography.Text>
      </Space>
    </Card>
  );
}

function AcceptedCard({
  orgName,
  institutionName,
  redirectTo,
}: {
  orgName: string;
  institutionName: string;
  redirectTo: string;
}) {
  const navigate = useNavigate();
  return (
    <Card>
      <Result
        icon={<CheckCircleFilled style={{ color: '#1c6442' }} />}
        title={`You're set up for ${orgName}`}
        subTitle={
          <span>
            Your invitation for <b>{institutionName}</b> is accepted. Setup step 1 is next.
          </span>
        }
        extra={[
          <Button
            key="go"
            type="primary"
            size="large"
            onClick={() => {
              window.location.href = redirectTo;
            }}
          >
            Continue to setup
          </Button>,
          <Button key="stay" onClick={() => navigate('/')}>
            Stay here
          </Button>,
        ]}
      />
      <div style={{ marginTop: 4, padding: 10, background: '#fdf7ea', border: '1px solid #eedcaf', borderRadius: 6 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
          <b>Heads-up:</b> the product isn't built yet in this iteration. The "Continue to setup"
          button lands at{' '}
          <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{redirectTo}</code>,
          which will 404 until <code>apps/web</code> renders that route. The invitation is really
          marked accepted in the database — the Platform Console will show this Organization as
          "Live" now.
        </Typography.Text>
      </div>
    </Card>
  );
}
